import {
  AnalyticsActionBuyEvent,
  AnalyticsEventParam,
  GiftPaymentResult,
  PurchaseProduct,
  ReceiptStatus,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { useCallback } from 'react';

import {
  durationToIso8601,
  getScreenTitle,
} from '#/plugins/analytics/analyticsUtils';
import { PaymentStore } from '#/plugins/paywall/models';
import { falsyToNull } from '#/service/utils';
import { usePaywallStore } from '#/store/paywall/paywall.store';
import { tb } from '#/store/translation/translation.store';

export const isGiftResponse = (
  result: PurchaseProduct | GiftPaymentResult
): result is GiftPaymentResult => 'giftName' in result;

/**
 * Get the specific payload for the buy event based on the type of purchase (regular purchase or gift purchase)
 * @param params The purchase result which can be either a regular purchase or a gift purchase
 * @returns An object containing the specific payload for the buy event based on the type of purchase
 */
const getSpecificBuyPayload = (params: PurchaseProduct | GiftPaymentResult) => {
  if (isGiftResponse(params)) {
    return {
      [AnalyticsEventParam.SubscriptionPlan]: falsyToNull(params.giftName),
    };
  }

  return {
    [AnalyticsEventParam.SubscriptionPeriod]: durationToIso8601(
      params.duration
    ),
    // for backward compatibility, we will continue to send the plan name in the SubscriptionPlan parameter
    [AnalyticsEventParam.SubscriptionPlan]: falsyToNull(
      params.productName ?? params.planName
    ),
  };
};

export const useSendBuyEvent = () => {
  const { paywallApi } = usePaywallStore();

  const sendBuyEvent = useCallback(
    async (params: PurchaseProduct | GiftPaymentResult) => {
      const { name: providerName } = await paywallApi.getProviderInfo();

      document.dispatchEvent(
        new AnalyticsActionBuyEvent({
          [AnalyticsEventParam.ScreenHeading]: tb('iap.setUpYourPayment'),
          [AnalyticsEventParam.ScreenTitle]: getScreenTitle(),
          [AnalyticsEventParam.ScreenType]: ScreenType.Purchase,
          [AnalyticsEventParam.VoucherCode]: falsyToNull(params.voucherCode),
          [AnalyticsEventParam.Price]: params.amountCharged,
          [AnalyticsEventParam.Currency]: params.currency,
          [AnalyticsEventParam.TransactionId]: params.transactionId,
          [AnalyticsEventParam.Store]: providerName ?? PaymentStore.Unknown,
          [AnalyticsEventParam.ReceiptStatus]: ReceiptStatus.Successful,
          [AnalyticsEventParam.ProductId]: falsyToNull(params.productId),
          ...getSpecificBuyPayload(params),
        })
      );
    },
    [paywallApi]
  );

  return { sendBuyEvent };
};
